/**
 * ForecastChart — Cumulative MTD forecast with high/medium/low prediction bands.
 *
 * Shows actual cumulative sales building up through the month, then projects
 * forward using linear regression with 80% prediction intervals. Open orders
 * are included in the forecast baseline. A horizontal budget reference line
 * shows the monthly target.
 */

import { useMemo } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";
import type { Props as LegendProps } from "recharts/types/component/DefaultLegendContent";
import { DailyTrendPoint, PeriodMetrics } from "../utils/PowerBIApiCall";
import {
  buildForecast,
  ForecastDataPoint,
  ForecastResult,
} from "../utils/forecastModel";
import {
  formatCurrencyAxis as formatYAxis,
  formatCurrency,
} from "../utils/format";
import { SERIES, GRID, AXIS_TICK } from "../utils/chartTheme";

// ─── Props ──────────────────────────────────────────────────────────────────

interface ForecastChartProps {
  /** Rolling daily trend data */
  data: DailyTrendPoint[];
  /** MTD metrics (business days, actuals, etc.) */
  mtdMetrics: PeriodMetrics;
  /** Full monthly budget for the reference line */
  budgetFull: number;
  /** Total open orders to include in forecast */
  openOrders: number;
}

// ─── Custom Tooltip ─────────────────────────────────────────────────────────

interface TooltipEntry {
  color: string;
  name: string;
  value: number;
  dataKey: string;
}

function ForecastTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;

  // Filter out the band area (we show high/low as a range instead)
  const actual = payload.find((p) => p.dataKey === "actual");
  const forecast = payload.find((p) => p.dataKey === "forecast");
  const high = payload.find((p) => p.dataKey === "forecastHigh");
  const low = payload.find((p) => p.dataKey === "forecastLow");
  const budget = payload.find((p) => p.dataKey === "budget");

  return (
    <div className="bg-white border border-gray-200/70 rounded-xl shadow-pop p-3 min-w-[160px]">
      <p className="text-xs font-semibold text-gray-700 mb-1.5">{label}</p>

      {actual && actual.value != null && (
        <p className="text-xs" style={{ color: SERIES.primary }}>
          <span className="font-medium">Actual:</span>{" "}
          <span className="tabular-nums">{formatCurrency(actual.value)}</span>
        </p>
      )}

      {forecast && forecast.value != null && (
        <p className="text-xs" style={{ color: SERIES.primary }}>
          <span className="font-medium">Forecast:</span>{" "}
          <span className="tabular-nums">{formatCurrency(forecast.value)}</span>
        </p>
      )}

      {high && low && high.value != null && low.value != null && (
        <p className="text-xs text-gray-400">
          <span className="font-medium">Range:</span>{" "}
          <span className="tabular-nums">
            {formatCurrency(low.value)} – {formatCurrency(high.value)}
          </span>
        </p>
      )}

      {budget && budget.value != null && (
        <p className="text-xs" style={{ color: SERIES.budget }}>
          <span className="font-medium">Budget:</span>{" "}
          <span className="tabular-nums">{formatCurrency(budget.value)}</span>
        </p>
      )}
    </div>
  );
}

// ─── Summary Cards ──────────────────────────────────────────────────────────

function ForecastSummaryCards({
  result,
  budgetFull,
}: {
  result: ForecastResult;
  budgetFull: number;
}) {
  const { summary } = result;
  const fcstVsBudgetPct =
    budgetFull > 0
      ? ((summary.forecastTotal - budgetFull) / budgetFull) * 100
      : 0;
  const isAboveBudget = fcstVsBudgetPct >= 0;

  return (
    <div className="grid grid-cols-3 gap-3 px-6 pb-4">
      {/* Low */}
      <div className="bg-gray-50/80 rounded-lg p-3 text-center">
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
          Low
        </p>
        <p className="text-sm font-bold text-gray-600 mt-1">
          {formatCurrency(summary.forecastLow)}
        </p>
      </div>
      {/* Medium (main) */}
      <div className="bg-blue-50/60 border border-blue-100 rounded-lg p-3 text-center">
        <p className="text-[10px] font-semibold text-wmp-500 uppercase tracking-wider">
          Forecast
        </p>
        <p className="text-sm font-bold text-gray-900 mt-1">
          {formatCurrency(summary.forecastTotal)}
        </p>
        <p
          className={`text-[10px] font-semibold mt-0.5 ${isAboveBudget ? "text-emerald-600" : "text-red-600"}`}
        >
          {fcstVsBudgetPct > 0 ? "+" : ""}
          {fcstVsBudgetPct.toFixed(1)}% vs bgt
        </p>
      </div>
      {/* High */}
      <div className="bg-gray-50/80 rounded-lg p-3 text-center">
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
          High
        </p>
        <p className="text-sm font-bold text-gray-600 mt-1">
          {formatCurrency(summary.forecastHigh)}
        </p>
      </div>
    </div>
  );
}

// ─── Recharts Legend Formatter ───────────────────────────────────────────────

function renderLegend(props: LegendProps) {
  const payload = props?.payload as
    Array<{ value: string; color: string; dataKey?: string }> | undefined;
  if (!payload) return null;

  // Only show select legend items (hide the band areas)
  const items = payload.filter((entry) => {
    const key = entry.dataKey ?? entry.value;
    return ["actual", "forecast", "budget"].includes(key);
  });

  return (
    <div className="flex items-center justify-center gap-5 mt-2">
      {items.map((entry) => {
        const label =
          entry.value === "actual"
            ? "Actual"
            : entry.value === "forecast"
              ? "Forecast"
              : entry.value === "budget"
                ? "Budget (Full)"
                : entry.value;
        const isDashed = entry.value === "forecast" || entry.value === "budget";
        return (
          <div key={entry.value} className="flex items-center gap-1.5">
            <svg width="16" height="3">
              <line
                x1="0"
                y1="1.5"
                x2="16"
                y2="1.5"
                stroke={entry.color}
                strokeWidth={2}
                strokeDasharray={isDashed ? "4 3" : undefined}
              />
            </svg>
            <span className="text-xs text-gray-500">{label}</span>
          </div>
        );
      })}
      <div className="flex items-center gap-1.5">
        <svg width="16" height="10">
          <rect
            width="16"
            height="10"
            rx="2"
            fill={SERIES.primary}
            opacity={0.12}
          />
        </svg>
        <span className="text-xs text-gray-500">95% Range</span>
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function ForecastChart({
  data,
  mtdMetrics,
  budgetFull,
  openOrders,
}: ForecastChartProps) {
  const result = useMemo(
    () => buildForecast(data, mtdMetrics, budgetFull),
    [data, mtdMetrics, budgetFull],
  );

  if (!result || result.data.length < 3) return null;

  const { summary } = result;
  const elapsedLabel = `${mtdMetrics.businessDaysElapsed}/${mtdMetrics.businessDaysTotal} business days`;
  const openOrdersLabel =
    openOrders > 0 ? ` · Open Orders: ${formatCurrency(openOrders)}` : "";

  // Determine forecast performance color
  const fcstVsBudgetPct =
    budgetFull > 0
      ? ((summary.forecastTotal - budgetFull) / budgetFull) * 100
      : 0;
  const perfColor =
    fcstVsBudgetPct >= 0
      ? SERIES.positive
      : fcstVsBudgetPct >= -5
        ? SERIES.warning
        : SERIES.negative;

  return (
    <div className="card overflow-hidden mb-6">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Month-End Forecast
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {elapsedLabel}
            {openOrdersLabel}
          </p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-gray-900">
            {formatCurrency(summary.forecastTotal)}
          </p>
          <p className="text-xs font-semibold" style={{ color: perfColor }}>
            {fcstVsBudgetPct > 0 ? "+" : ""}
            {fcstVsBudgetPct.toFixed(1)}% vs budget
          </p>
        </div>
      </div>

      {/* Chart */}
      <div className="px-4 py-5">
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart
            data={result.data}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <defs>
              {/* Gradient for actual cumulative area */}
              <linearGradient id="gradFcstActual" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={SERIES.primary}
                  stopOpacity={0.25}
                />
                <stop offset="95%" stopColor={SERIES.primary} stopOpacity={0} />
              </linearGradient>
              {/* Gradient for prediction band */}
              <linearGradient id="gradFcstBand" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={SERIES.primary}
                  stopOpacity={0.12}
                />
                <stop
                  offset="95%"
                  stopColor={SERIES.primary}
                  stopOpacity={0.04}
                />
              </linearGradient>
            </defs>

            <CartesianGrid {...GRID} />

            <XAxis
              dataKey="label"
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
              interval="preserveStartEnd"
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={false}
              width={65}
            />

            <Tooltip content={<ForecastTooltip />} />

            {/* Budget reference line */}
            <ReferenceLine
              y={budgetFull}
              stroke={SERIES.budget}
              strokeDasharray="6 4"
              strokeWidth={1.5}
              label={{
                value: `Budget: ${formatYAxis(budgetFull)}`,
                position: "right",
                fontSize: 10,
                fill: SERIES.budget,
              }}
            />

            {/* Prediction band — upper bound (filled area between high and low) */}
            <Area
              type="monotone"
              dataKey="forecastHigh"
              name="forecastHigh"
              stroke="none"
              fill="url(#gradFcstBand)"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
            {/* Prediction band — lower bound (masks the bottom of the band) */}
            <Area
              type="monotone"
              dataKey="forecastLow"
              name="forecastLow"
              stroke="none"
              fill="#f8f9fb"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />

            {/* Medium forecast line — dashed continuation */}
            <Area
              type="monotone"
              dataKey="forecast"
              name="forecast"
              stroke={SERIES.primary}
              strokeWidth={2}
              strokeDasharray="6 4"
              fill="none"
              dot={false}
              activeDot={{ r: 5, fill: SERIES.primary }}
              connectNulls={false}
            />

            {/* Actual cumulative line — solid */}
            <Area
              type="monotone"
              dataKey="actual"
              name="actual"
              stroke={SERIES.primary}
              strokeWidth={2.5}
              fill="url(#gradFcstActual)"
              dot={{ r: 3, fill: SERIES.primary }}
              activeDot={{ r: 6 }}
              connectNulls={false}
            />

            {/* Hidden budget series for tooltip (not rendered visually) */}
            <Area
              type="monotone"
              dataKey="budget"
              name="budget"
              stroke="none"
              fill="none"
              dot={false}
              activeDot={false}
              legendType="none"
            />

            <Legend content={renderLegend} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Summary cards */}
      <ForecastSummaryCards result={result} budgetFull={budgetFull} />

      {/* Footnote */}
      <div className="px-6 pb-4">
        <p className="text-[11px] text-gray-400 italic leading-relaxed">
          Forecast uses linear regression on {summary.dataPointsUsed} business
          days of sales data (R² = {summary.rSquared.toFixed(2)}).
          {openOrders > 0 && (
            <>
              {" "}
              Open orders of {formatCurrency(openOrders)} are shown separately
              and are NOT added — the projected daily rate already covers the
              shipments they turn into.
            </>
          )}{" "}
          Band shows 95% prediction interval.
        </p>
      </div>
    </div>
  );
}
